import { IList } from 'annotation-tools/utils';
import React, { memo, useMemo, useState } from 'react';
import { map, withLatestFrom } from 'rxjs/operators';
import styled from 'styled-components';
import { appBarHeightPx } from '../../constants/styles/app-bar-height';
import { BreadcrumbsJp } from '../breadcrumbs/breadcrumbs';
import { HelpDialog } from './components/help-dialog';
import { ImageCropper } from './components/image-cropper';
import { Sidebar } from './components/sidebar/sidebar';
import { Toolbar } from './components/toolbar/toolbar';
import { defaultCropRectRelative } from './constants/default-crop-rect';
import {
  keyboardActions,
  mouseActions
} from './constants/messages/help-descriptions';
import {
  fromRectRelativeToCroppedRectFnGenerator,
  toRectRelativeToCroppedRectFnGenerator
} from './functions/to-rect-relative-to-cropped-rect';
import {
  AnnotationCanvas,
  IBboxType,
  IdType
} from './imports/annotation-canvas';
import {
  ConfirmDialog,
  DivCropped,
  ImgWithLoadingState,
  IRectSize,
  IRectSizeType,
  IRectType,
  useDataStream,
  useEventAsStream,
  useStream,
  useStreamEffect,
  useValueAsStream
} from './imports/utils';
import { RectToolWithCropMainViewProp } from './rect-tool-with-crop-main-view-props';

const Root = styled.div`
  width: 100%;
  height: calc(100vh - ${appBarHeightPx}px);
`;

const Body = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
`;

const sideBarWidthPx = 240;

const LeftSide = styled.div`
  width: calc(100vw - ${sideBarWidthPx}px);
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ToolbarWrapper = styled.div`
  flex-basis: 58px;
  flex-grow: 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  flex-wrap: wrap;
`;

const AnnotationCanvasWrapper = styled.div`
  flex-grow: 1;
  width: 100%;
  padding: 10px;
  overflow-y: auto;
`;

const RightSide = styled.div`
  width: ${sideBarWidthPx}px;
  max-height: 100%;
`;

// disable context menu
const eventCallbackWithPreventDefault = (
  ev: React.MouseEvent<HTMLDivElement, MouseEvent>
) => {
  ev.preventDefault();
};

export const RectToolWithCropMainView = memo(
  ({
    image,
    cropRectRelative,
    onCropRectRelativeChange,
    styles,
    labels,
    idMaker,
    appMode,
    onAppModeChange,
    action$,
    bboxesRelativeToImage,
    onBboxesRelativeToImageChange,
    onToolbarButtonClick,
    onPageChange,
    onLabelClick,
    onVisibilityClick,
    onShowAllClick,
    onHideAllClick,
    visibleLabels,
    selectedLabel,
    helpDialogIsOpen,
    closeHelpDialog,
    removeAllConfirmationMessage,
    confirmDialogIsOpen,
    closeConfirmDialog,
    currentPageIndex,
    pageLength,
    navButtonsIsDisabled,
    inferButtonState,
    openSettingsDialogButtonState
  }: RectToolWithCropMainViewProp) => {
    /* props */
    const cropRectRelative$ = useValueAsStream(cropRectRelative);

    // share id between AnnotationCanvas and annotation list of sidebar
    const [selectedId, setSelectedId] = useState<IdType | undefined>(undefined);
    const [highlightId, setHighlightId] = useState<IdType | undefined>(
      undefined
    );

    /* events */

    const [
      bboxesRelativeToCropedRectFromCanvas$,
      onBboxesRelativeToCropRectChange
    ] = useEventAsStream<IList<IBboxType>>();

    /* values */

    // 切り抜き領域を1x1としたときの矩形を画像全体を1x1としたときの矩形に変換する関数
    const fromRectRelativeToCroppedRectFn$ = useDataStream<
      (rectRelative: IRectType) => IRectType
    >(
      fromRectRelativeToCroppedRectFnGenerator(defaultCropRectRelative),
      cropRectRelative$.pipe(map(fromRectRelativeToCroppedRectFnGenerator))
    );

    const bboxesRelativeToImageFromCanvas$ = useStream<IList<IBboxType>>(
      bboxesRelativeToCropedRectFromCanvas$.pipe(
        withLatestFrom(fromRectRelativeToCroppedRectFn$),
        map(([bboxesRelativeToCropRect, converter]) =>
          bboxesRelativeToCropRect.map(b => b.update('rect', converter))
        )
      )
    );

    const { labelSaturation, labelLightness } = useMemo(() => styles, [styles]);

    const croppedPixelSize = useMemo<IRectSizeType>(
      () =>
        IRectSize({
          width: image.rectSize.width * cropRectRelative.width,
          height: image.rectSize.height * cropRectRelative.height
        }),
      [image.rectSize, cropRectRelative]
    );

    const buttonsDisabled = appMode === 'crop-image';

    const toRectRelativeToCroppedRectFn = useMemo(
      () => toRectRelativeToCroppedRectFnGenerator(cropRectRelative),
      [cropRectRelative]
    );

    const bboxesRelativeToCropRect = useMemo<IList<IBboxType>>(
      () =>
        bboxesRelativeToImage.map(bbox =>
          // 画像全体を1x1としたときの矩形を切り抜き領域を1x1としたときの矩形に変換する
          bbox.update('rect', toRectRelativeToCroppedRectFn)
        ),
      [bboxesRelativeToImage, toRectRelativeToCroppedRectFn]
    );

    /* side effects */

    useStreamEffect(
      bboxesRelativeToImageFromCanvas$,
      onBboxesRelativeToImageChange
    );

    return (
      <Root onContextMenu={eventCallbackWithPreventDefault}>
        <Body>
          <LeftSide>
            <BreadcrumbsJp showPadding={true} />
            <ToolbarWrapper>
              <Toolbar
                labels={labels}
                labelSaturation={labelSaturation}
                labelLightness={labelLightness}
                currentPageIndex={currentPageIndex}
                pageLength={pageLength}
                appMode={appMode}
                onAppModeChange={onAppModeChange}
                onLabelClick={onLabelClick}
                onToolbarButtonClick={onToolbarButtonClick}
                onPageChange={onPageChange}
                editButtonsDisabled={buttonsDisabled}
                navButtonsIsDisabled={navButtonsIsDisabled}
                inferButtonState={inferButtonState}
                openSettingsDialogButtonState={openSettingsDialogButtonState}
              />
            </ToolbarWrapper>
            <AnnotationCanvasWrapper>
              {appMode === 'crop-image' ? (
                <ImageCropper
                  styles={styles}
                  fullImageData={image}
                  cropRectRelative={cropRectRelative}
                  onCropRectRelativeChange={onCropRectRelativeChange}
                />
              ) : (
                <AnnotationCanvas
                  styles={styles}
                  idMaker={idMaker}
                  imagePixelSize={croppedPixelSize}
                  bboxesRelative={bboxesRelativeToCropRect}
                  onBboxesRelativeChange={onBboxesRelativeToCropRectChange}
                  visibleLabels={visibleLabels}
                  selectedLabel={selectedLabel}
                  selectedId={selectedId}
                  highlightId={highlightId}
                  onSelectedIdChange={setSelectedId}
                  onHighlightIdChange={setHighlightId}
                  zoomable={true}
                  annotationMode='rectbox'
                  action$={action$}
                >
                  <DivCropped
                    component={<ImgWithLoadingState fullImgSrc={image.src} />}
                    cropRectRelative={cropRectRelative}
                  />
                </AnnotationCanvas>
              )}
            </AnnotationCanvasWrapper>
          </LeftSide>
          <RightSide>
            <Sidebar
              labels={labels}
              labelSaturation={labelSaturation}
              labelLightness={labelLightness}
              visibleLabels={visibleLabels}
              selectedLabel={selectedLabel}
              bboxesRelative={bboxesRelativeToImage}
              selectedId={selectedId}
              highlightId={highlightId}
              onLabelClick={onLabelClick}
              onVisibilityClick={onVisibilityClick}
              onShowAllClick={onShowAllClick}
              onHideAllClick={onHideAllClick}
              onSelectedIdChange={setSelectedId}
              onHighlightIdChange={setHighlightId}
              disabled={buttonsDisabled}
            />
          </RightSide>
        </Body>
        <HelpDialog
          keyboardActions={keyboardActions}
          mouseActions={mouseActions}
          open={helpDialogIsOpen}
          onClose={closeHelpDialog}
        />
        <ConfirmDialog
          message={removeAllConfirmationMessage}
          open={confirmDialogIsOpen}
          onClose={closeConfirmDialog}
        />
      </Root>
    );
  }
);

RectToolWithCropMainView.displayName = 'RectToolWithCropMainView';
